/*
 * Wag & Whistle — shared product card behaviour.
 *
 * Handles the round "+" button on .ww-card wherever the card
 * appears (All Products grid, homepage grid, related products), and
 * the "Goes well with" rows on the product page.
 *
 * Controls:
 *   [data-variant-id]  — adds that variant straight to the cart.
 *   [data-card-choose] — opens the variant chooser. Used by both
 *                        the "+" and the "Add another …" button, so
 *                        switching option is the same gesture as
 *                        picking one in the first place.
 *   [data-qty-step]    — the − N + stepper on a basket row.
 *
 * The card footer is rebuilt from /cart.js rather than tracked in
 * memory. One row per option held, each with its own price and its
 * own stepper, so a card carrying two bundles shows both. Reading
 * from the cart is also what keeps the numbers right when the cart
 * changes elsewhere — the drawer, another tab, or a page reload.
 *
 * THE DRAWER IS SERVER-RENDERED, so changing the cart through the
 * API is not enough on its own: the drawer's markup is whatever it
 * was when the page loaded, which is why quick-adds used to leave
 * it saying "your cart is empty". After every change we re-request
 * the cart-drawer section and swap its contents in, the same way
 * Dawn's own add-to-cart does.
 *
 * Each section that renders cards includes this file, so it can be
 * requested more than once on a page. The guard below makes that
 * harmless: without it, the click listener would be registered
 * multiple times and one tap would add several items to the cart.
 */
(function () {
  if (window.wwCardsInitialised) return;
  window.wwCardsInitialised = true;

  var CART_HEADERS = { 'Content-Type': 'application/json' };
  var FLASH_MS = 1400;

  var MINUS_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>';
  var PLUS_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>';

  function announceCartChange() {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ww-card' });
    }
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  function fetchCart() {
    return fetch('/cart.js').then(function (r) {
      return r.json();
    });
  }

  /* Pull the cart drawer's markup back from the server and swap it
     in. Without this the drawer keeps whatever it rendered with at
     page load — for a first add, an empty basket.

     The header's count bubble is updated from the cart itself
     rather than from a section, because this theme's header is
     custom and renders the bubble inline. */
  function refreshDrawer(cart) {
    return fetch('/?sections=cart-drawer')
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        var markup = sections && sections['cart-drawer'];
        if (!markup) return;

        var parsed = new DOMParser().parseFromString(markup, 'text/html');
        var fresh = parsed.querySelector('cart-drawer');
        var live = document.querySelector('cart-drawer');
        if (!fresh || !live) return;

        var wasOpen = live.classList.contains('active');
        live.innerHTML = fresh.innerHTML;
        live.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
        if (wasOpen) live.classList.add('active');
      })
      .then(function () {
        updateBubble(cart);
      })
      .catch(function () {});
  }

  function updateBubble(cart) {
    if (!cart) return;
    var icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;

    var bubble = icon.querySelector('.cart-count-bubble');
    var count = cart.item_count || 0;

    if (count === 0) {
      if (bubble) bubble.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      icon.appendChild(bubble);
    }

    bubble.innerHTML =
      (count < 100 ? '<span aria-hidden="true">' + count + '</span>' : '') +
      '<span class="visually-hidden">' + count + ' items</span>';
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function variantDataFor(card) {
    if (card._wwVariants) return card._wwVariants;
    var el = card.querySelector('.ww-card__variant-data');
    var data = [];
    if (el) {
      try {
        data = JSON.parse(el.textContent);
      } catch (err) {
        data = [];
      }
    }
    card._wwVariants = data;
    return data;
  }

  function buildLine(variant, qty) {
    return (
      '<div class="ww-card__line" data-variant-id="' + escapeHtml(variant.id) + '">' +
        '<span class="ww-card__line-info">' +
          '<span class="ww-card__line-name">' + escapeHtml(variant.title) + '</span>' +
          '<span class="ww-card__line-flash" role="status"></span>' +
          '<span class="ww-card__line-price">' + escapeHtml(variant.price) + '</span>' +
        '</span>' +
        '<span class="ww-card__qty">' +
          '<button type="button" class="ww-card__qty-btn" data-qty-step="-1" aria-label="Decrease quantity of ' + escapeHtml(variant.title) + '">' + MINUS_ICON + '</button>' +
          '<span class="ww-card__qty-count" aria-live="polite">' + qty + '</span>' +
          '<button type="button" class="ww-card__qty-btn" data-qty-step="1" aria-label="Increase quantity of ' + escapeHtml(variant.title) + '">' + PLUS_ICON + '</button>' +
        '</span>' +
      '</div>'
    );
  }

  /* Confirmation swaps the option name for a message and then swaps
     back. The price and the count stay put throughout — they are
     the numbers being confirmed, so hiding them to announce the
     change would defeat the point. Applied after the repaint, since
     the repaint rebuilds these rows from scratch. */
  function flashLine(card, variantId, message) {
    if (!card) return;
    var line = card.querySelector('.ww-card__line[data-variant-id="' + variantId + '"]');
    if (!line) return;
    var flashEl = line.querySelector('.ww-card__line-flash');
    if (flashEl) flashEl.textContent = message;
    line.classList.add('is-flashing');
    clearTimeout(line._wwFlashTimer);
    line._wwFlashTimer = setTimeout(function () {
      line.classList.remove('is-flashing');
    }, FLASH_MS);
  }

  /* Taking the last one out removes the row entirely, so there is no
     row left to carry the message. The card has already reverted to
     its "from" price at that point, so the confirmation goes there
     instead — briefly taking the "from" label's place. */
  function flashCardDefault(card) {
    if (!card) return;
    card.classList.add('is-removed');
    clearTimeout(card._wwFlashTimer);
    card._wwFlashTimer = setTimeout(function () {
      card.classList.remove('is-removed');
    }, FLASH_MS);
  }

  /* Rebuild a card's footer to match what the cart holds for it.
     Rows follow the product's own variant order, not the order they
     were added, so a card doesn't reshuffle itself as someone
     shops. */
  function paintCard(card, quantities) {
    var linesEl = card.querySelector('.ww-card__lines');
    if (!linesEl) return;

    var variants = variantDataFor(card);
    var html = '';
    var held = 0;

    variants.forEach(function (variant) {
      var qty = quantities[String(variant.id)];
      if (qty > 0) {
        html += buildLine(variant, qty);
        held++;
      }
    });

    linesEl.innerHTML = html;
    card.classList.toggle('is-in-cart', held > 0);

    /* The chooser button only earns its place when an option remains
       that isn't already in the basket. */
    card.classList.toggle('has-spare-options', held > 0 && held < variants.length);

    card.querySelectorAll('.ww-card__choose-option').forEach(function (option) {
      option.classList.toggle('is-in-cart', !!quantities[String(option.dataset.variantId)]);
    });
  }

  function syncCards(cart) {
    var quantities = {};
    (cart.items || []).forEach(function (item) {
      quantities[String(item.id)] = item.quantity;
    });

    document.querySelectorAll('.ww-card[data-card-shoppable]').forEach(function (card) {
      paintCard(card, quantities);
    });

    return cart;
  }

  function resync() {
    return fetchCart().then(syncCards).catch(function () {});
  }

  /* Every cart change goes through here: repaint the cards, then
     re-render the drawer so the two can never disagree. */
  function resyncAll() {
    return fetchCart()
      .then(syncCards)
      .then(function (cart) {
        return refreshDrawer(cart);
      })
      .catch(function () {});
  }

  function addVariant(variantId, btn) {
    if (!variantId) return;
    var card = btn ? btn.closest('.ww-card') : null;
    if (btn) btn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: CART_HEADERS,
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (r) {
        return r.json();
      })
      .then(function () {
        announceCartChange();
        return resyncAll();
      })
      .then(function () {
        flashLine(card, variantId, 'Added to cart');
      })
      .catch(function () {})
      .then(function () {
        if (btn) btn.disabled = false;
      });
  }

  /* The count updates before the request completes, so tapping feels
     instant and repeated taps stay responsive. The resync afterwards
     is what makes it truthful — if the request failed, the row
     snaps back to whatever the cart actually holds. */
  function stepQuantity(line, delta) {
    var variantId = line.dataset.variantId;
    if (!variantId) return;

    var card = line.closest('.ww-card');
    var countEl = line.querySelector('.ww-card__qty-count');
    var current = parseInt(countEl ? countEl.textContent : '0', 10) || 0;
    var next = current + delta;
    if (next < 0) next = 0;

    if (countEl) countEl.textContent = next;

    fetch('/cart/change.js', {
      method: 'POST',
      headers: CART_HEADERS,
      body: JSON.stringify({ id: variantId, quantity: next })
    })
      .then(function (r) {
        return r.json();
      })
      .then(function () {
        announceCartChange();
        return resyncAll();
      })
      .then(function () {
        if (delta > 0) {
          flashLine(card, variantId, 'Added to cart');
        } else if (next === 0) {
          /* "Removed from cart" is only literally true here — the
             last one has gone. Above zero the item is still in the
             basket, so the wording says what actually happened. */
          flashCardDefault(card);
        } else {
          flashLine(card, variantId, 'Removed 1 from cart');
        }
      })
      .catch(function () {
        resyncAll();
      });
  }

  function closeChoosers(except) {
    document.querySelectorAll('.ww-card.is-choosing').forEach(function (card) {
      if (card === except) return;
      card.classList.remove('is-choosing');
      var panel = card.querySelector('.ww-card__choose');
      if (panel) panel.hidden = true;
      card.querySelectorAll('[data-card-choose]').forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      });
    });
  }

  document.addEventListener('click', function (e) {
    var stepBtn = e.target.closest('[data-qty-step]');
    if (stepBtn) {
      var line = stepBtn.closest('.ww-card__line');
      if (line) stepQuantity(line, parseInt(stepBtn.dataset.qtyStep, 10));
      return;
    }

    /* Both the "+" and "Add another …" carry data-card-choose */
    var chooseBtn = e.target.closest('[data-card-choose]');
    if (chooseBtn) {
      var card = chooseBtn.closest('.ww-card');
      if (!card) return;
      var alreadyOpen = card.classList.contains('is-choosing');
      closeChoosers();
      if (!alreadyOpen) {
        card.classList.add('is-choosing');
        var panel = card.querySelector('.ww-card__choose');
        if (panel) panel.hidden = false;
        chooseBtn.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    if (e.target.closest('[data-card-cancel]')) {
      closeChoosers();
      return;
    }

    var option = e.target.closest('.ww-card__choose-option');
    if (option) {
      closeChoosers();
      addVariant(option.dataset.variantId, option);
      return;
    }

    /* Single-variant cards and the product page add-on rows */
    var addBtn = e.target.closest('.ww-card__add[data-variant-id]');
    if (addBtn) {
      addVariant(addBtn.dataset.variantId, addBtn);
      return;
    }

    if (!e.target.closest('.ww-card')) closeChoosers();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeChoosers();
  });

  resync();

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (payload) {
      if (payload && payload.source === 'ww-card') return;
      resync();
    });
  }
})();
